/**
 * React Query hooks for review progress tracking
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  startReviewSession,
  getActiveSessions,
  getSession,
  updateSessionProgress,
  completeSession,
  getSectionCoverage,
  getNextUnreviewed,
  getUserReviewStats,
} from '@/api/reviewProgress';
import {
  ReviewProgressCreate,
  ReviewProgressUpdate,
  ReviewProgressResponse,
  ReviewSessionSummary,
  CoverageResponse,
  NextUnreviewedResponse,
  UserReviewStats,
} from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch active review sessions
 */
export const useActiveSessions = (chatId?: number) => {
  return useQuery<ReviewSessionSummary[], Error>({
    queryKey: ['review-progress', 'active', chatId],
    queryFn: () => getActiveSessions(chatId),
    staleTime: 30000, // 30 seconds - refresh frequently to show latest state
  });
};

/**
 * Hook to fetch a specific session
 */
export const useSession = (sessionId: number | null) => {
  return useQuery<ReviewProgressResponse, Error>({
    queryKey: ['review-progress', 'session', sessionId],
    queryFn: () => getSession(sessionId!),
    enabled: sessionId !== null && sessionId > 0,
  });
};

/**
 * Hook to start a new review session
 */
export const useStartSession = () => {
  const queryClient = useQueryClient();

  return useMutation<ReviewProgressResponse, Error, ReviewProgressCreate>({
    mutationFn: startReviewSession,
    onSuccess: (data) => {
      // Invalidate active sessions to show the new session
      queryClient.invalidateQueries({ queryKey: ['review-progress', 'active'] });
      // Add the new session to cache
      queryClient.setQueryData(['review-progress', 'session', data.id], data);
    },
    onError: (error) => {
      console.error('Failed to start review session:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to update session progress
 */
export const useUpdateProgress = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { status: string; session_id: number; progress_percentage: number },
    Error,
    { sessionId: number; data: ReviewProgressUpdate }
  >({
    mutationFn: ({ sessionId, data }) => updateSessionProgress(sessionId, data),
    onSuccess: (_, variables) => {
      // Invalidate both the specific session and active sessions list
      queryClient.invalidateQueries({
        queryKey: ['review-progress', 'session', variables.sessionId],
      });
      queryClient.invalidateQueries({ queryKey: ['review-progress', 'active'] });
    },
    onError: (error) => {
      console.error('Failed to update progress:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to complete a review session
 */
export const useCompleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation<
    {
      status: string;
      session_id: number;
      total_messages: number;
      reviewed_messages: number;
      accepted_count: number;
      rejected_count: number;
      progress_percentage: number;
    },
    Error,
    number
  >({
    mutationFn: completeSession,
    onSuccess: (_, sessionId) => {
      // Invalidate to refresh session state
      queryClient.invalidateQueries({
        queryKey: ['review-progress', 'session', sessionId],
      });
      queryClient.invalidateQueries({ queryKey: ['review-progress', 'active'] });
      // Also invalidate coverage stats as they've changed
      queryClient.invalidateQueries({ queryKey: ['review-progress', 'coverage'] });
      queryClient.invalidateQueries({ queryKey: ['review-progress', 'stats'] });
    },
    onError: (error) => {
      console.error('Failed to complete session:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to fetch section coverage statistics
 */
export const useSectionCoverage = (sectionId: number | null) => {
  return useQuery<CoverageResponse, Error>({
    queryKey: ['review-progress', 'coverage', 'section', sectionId],
    queryFn: () => getSectionCoverage(sectionId!),
    enabled: sectionId !== null && sectionId > 0,
    staleTime: 60000, // 1 minute - coverage doesn't change very often
  });
};

/**
 * Hook to get next unreviewed message range
 */
export const useNextUnreviewed = (
  chatId: number | null,
  librarySectionId?: number,
  preferredSize: number = 2000
) => {
  return useQuery<NextUnreviewedResponse, Error>({
    queryKey: [
      'review-progress',
      'next-unreviewed',
      chatId,
      librarySectionId,
      preferredSize,
    ],
    queryFn: () => getNextUnreviewed(chatId!, librarySectionId, preferredSize),
    enabled: chatId !== null && chatId > 0,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Hook to fetch user review statistics
 */
export const useUserReviewStats = () => {
  return useQuery<UserReviewStats, Error>({
    queryKey: ['review-progress', 'stats', 'user'],
    queryFn: getUserReviewStats,
    staleTime: 300000, // 5 minutes - stats don't change very frequently
  });
};
